import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import PageBody from 'components/atoms/PageBody'
import PageHeading from 'components/atoms/PageHeading'
import StudentForm from 'components/organisms/forms/StudentForm'

import { crudActionsMap } from 'utils/helpers'
import useCrudData from 'utils/customHooks/useCrudData'

import Tabs from 'components/organisms/tabs/Tabs'

import {
  useDELETE_studentMutation,
  useGET_studentByIdQuery,
  usePOST_studentMutation,
  usePUT_studentMutation,
} from 'redux/api/studentsApi'
import StudentReports from 'components/utilized/students/studentReports'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { MenuBook, School } from '@mui/icons-material'
import RtiPlopList from 'components/utilized/rti/RtiPlopList'
import StudentGradebook from 'components/utilized/students/StudentGradebook'
import StudentKpiTracker from 'components/utilized/students/studentKpiTracker'
import StudentBenchmarkTracker from 'components/utilized/students/studentBenchmarkTracker'
import StudentProgram from 'components/utilized/students/studentProgram'
import StudentLedger from 'components/utilized/students/StudentLedger'
import DialogPopup from 'components/molecules/DialogPopup'
import { useState } from 'react'

const StudentCrudPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const params = useParams()
  const id = params?.id

  const action = crudActionsMap[params['*']?.split('/')?.[1]]
  const viewOnly = action === 'view' || action === 'delete'


  const backToSlug ='/students'


  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('common.student')}`,
    entityId: id,
    getEntityQuery: useGET_studentByIdQuery,
    createEntityMutation: usePOST_studentMutation,
    editEntityMutation: usePUT_studentMutation,
    deleteEntityMutation: useDELETE_studentMutation
  })


  const student = entity?.id ? entity : null

  // Matches the Ledger API permissions
  const user = useSelector((state: RootState) => state['user'].user)
  const canViewLedger = ['teacher', 'principal', 'vice_principal'].includes(user?.role)
  const [ledgerOpen, setLedgerOpen] = useState(false)

  return (
    <>
      {
        student && 
        <Box className="breadcrumbs" sx={{ padding: '5px 20px', marginBottom: '20px', boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px', background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
              <School />
              <Typography sx={{ fontSize: '1rem', fontWeight: '600', marginLeft: '10px' }}>{student.first_name} {student.last_name} </Typography>
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(`/rti-students/${student.id}`)}
                className='small-button'
                sx={{ width: '120px', marginLeft: '20px' }}
              >
                {`${t('common.go-to-rti')}`}
              </Button>
              {canViewLedger && (
                <Tooltip title={`${t('ledger.ledger')}`} arrow placement='top'>
                  <IconButton
                    onClick={() => setLedgerOpen(true)}
                    sx={{ marginLeft: '10px' }}
                  >
                    <MenuBook />
                  </IconButton>
                </Tooltip>
              )}
          </div>
        </Box>
      }

      {student && canViewLedger && (
        <DialogPopup
          title={`${t('ledger.ledger')} / ${student.first_name} ${student.last_name}`}
          open={ledgerOpen}
          onClose={() => setLedgerOpen(false)}
          popupWidth={'1400px'}
        >
          {ledgerOpen && <StudentLedger studentProfileId={Number(student.student_profile_id)} />}
        </DialogPopup>
      )}

      <PageHeading title={`${t('common.students')} / ${t('common.' + action)}`} />

      <PageBody>
        <Tabs tabKey="details">
          <StudentForm tabKey="details" tabText={ t('common.details') } action={action} isLoading={isLoading} handleSubmit={handleSubmit} entity={student} viewOnly={viewOnly} />
          
          {entity?.id && 
            <RtiPlopList student={student} tabKey="plop" tabText={`${t('common.present-level-of-performance')}`} /> }

          {entity?.id && 
            <StudentProgram student={entity} 
              tabKey="program" tabText={`${t('common.personalized-learning-pathways')}`}/> }
        
          {entity?.id && 
            <StudentReports student_id={parseInt(entity.student_profile_id)} tabKey="reports" tabText={`${t('common.reports')}`}/> }
          
          {entity?.id && 
            <StudentBenchmarkTracker student={entity} 
              tabKey="benchmarkTracker" tabText={`${t('common.benchmark-tracker')}`}/> }

          {entity?.id && 
            <StudentKpiTracker student={entity} 
              tabKey="kpiTracker" tabText={`${t('common.kpi-tracker')}`}/> }
        
          {entity?.id && 
            <StudentGradebook viewOnly={viewOnly} student={entity.student_profile_id} tabKey="gradebook" tabText={`${t('common.gradebook')}`}/> }
        
        </Tabs>
      </PageBody>
    </>
  )
}
export default StudentCrudPage
